/**
 * Pure folder-ordering + tree-flattening helpers for the mailbox sidebar.
 * Extracted from MailboxPage so they can be unit-tested in isolation (the page
 * itself is a large component). `sortFolders` gives the flat, role-ordered list;
 * `buildFolderTree` nests children under parents into a depth-annotated list.
 */

import type { EmailFolderDto } from '@/lib/api';

/** System folders sort in this fixed order; custom folders fall after, A→Z. */
export const FOLDER_ROLE_ORDER = ['inbox', 'sent', 'drafts', 'archive', 'spam', 'trash'];

/** Order a folder set: system roles first (fixed order), then custom by name. */
export function sortFolders(folders: EmailFolderDto[]): EmailFolderDto[] {
  return [...folders].sort((a, b) => {
    const ai = a.systemRole ? FOLDER_ROLE_ORDER.indexOf(a.systemRole) : 99;
    const bi = b.systemRole ? FOLDER_ROLE_ORDER.indexOf(b.systemRole) : 99;
    return ai - bi || a.name.localeCompare(b.name);
  });
}

/** One row of the folder sidebar tree: a folder plus its nesting depth. */
export interface FolderTreeRow {
  folder: EmailFolderDto;
  depth: number;
  hasChildren: boolean;
}

const FOLDER_TREE_ROOT = '__root__';

/**
 * Flattens the account's folders into a depth-annotated pre-order list for the
 * sidebar: children (grouped by `parentFolderId`) nest under their parent, and
 * each level is ordered by the same `sortFolders` comparator as the flat list.
 * When `isCollapsed` is given, a collapsed folder's subtree is omitted (its own
 * row still renders, with `hasChildren` true, so its chevron shows). A folder
 * whose parent isn't in the set (orphaned by a delete) surfaces at the root, and
 * a `seen` guard makes any accidental parent cycle terminate instead of looping.
 */
export function buildFolderTree(
  folders: EmailFolderDto[],
  isCollapsed?: (folderId: string) => boolean,
): FolderTreeRow[] {
  const ids = new Set(folders.map((f) => f.folderId));
  const byParent = new Map<string, EmailFolderDto[]>();
  for (const f of folders) {
    const key = f.parentFolderId && ids.has(f.parentFolderId) ? f.parentFolderId : FOLDER_TREE_ROOT;
    const siblings = byParent.get(key);
    if (siblings) siblings.push(f);
    else byParent.set(key, [f]);
  }
  const rows: FolderTreeRow[] = [];
  const seen = new Set<string>();
  const visit = (key: string, depth: number) => {
    for (const folder of sortFolders(byParent.get(key) ?? [])) {
      if (seen.has(folder.folderId)) continue; // cycle guard
      seen.add(folder.folderId);
      const hasChildren = byParent.has(folder.folderId);
      rows.push({ folder, depth, hasChildren });
      if (hasChildren && !isCollapsed?.(folder.folderId)) visit(folder.folderId, depth + 1);
    }
  };
  visit(FOLDER_TREE_ROOT, 0);
  return rows;
}
